import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Play, Pause,
  LayoutGrid, Presentation, Printer, Maximize2, Minimize2,
  Calendar, Car, Wrench, Video, ClipboardCheck, Sparkles, Send, ShieldCheck,
  Camera, MessageCircle, Mail, CheckCircle2, AlertTriangle, Trophy, Droplets,
  CalendarClock, Search, Brain, PoundSterling, Phone, Rocket, Loader2,
} from "lucide-react";
import { BrandLink } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STAGES = [
  {
    id: "booking",
    n: "01",
    label: "Booking",
    title: "Customer books in.",
    sub: "Service desk creates the job in seconds.",
    body: "Whether the booking comes from the workshop's diary, a phone call, or a walk-in, the service advisor opens ServiceSell PRO and creates the job — capturing customer details, vehicle and the work expected.",
    bullets: [
      "Slot booked in the workshop diary",
      "Customer + vehicle captured against the job",
      "Service history auto-loads if the reg has been before",
    ],
    icon: Calendar,
    accent: "primary",
    visual: "booking",
  },
  {
    id: "diary-ai",
    n: "02",
    label: "Diary AI",
    title: "Snap tomorrow's diary. AI does the pre-calls.",
    sub: "Photo of your printed page → every booking imported → SMS + email blast.",
    body: "At 4pm the advisor takes one photo of the daily diary page. The ServiceSell PRO AI reads every row — time, customer, reg, work — extracts each booking, lets the advisor review/fix, then with one tap fires a personalised pre-call SMS + email to every customer. Diagnostic jobs get the 10-question pre-work form attached.",
    bullets: [
      "ServiceSell PRO AI parses the printed diary",
      "Review screen catches any misreads",
      "One-tap SMS + email blast to all customers",
      "Diagnostic questionnaire auto-attached",
    ],
    icon: CalendarClock,
    accent: "primary",
    visual: "diary-ai",
  },
  {
    id: "checkin",
    n: "03",
    label: "Check-in",
    title: "Vehicle arrives at the dealership.",
    sub: "Welcome message fires the moment keys land.",
    body: "On arrival the advisor confirms the job is live. ServiceSell PRO sends a personalised welcome SMS + email explaining exactly what'll happen today — the inspection, the video, the traffic-light report, and how easy it'll be to authorise work.",
    bullets: [
      "Personalised welcome SMS + email",
      "Sets expectations: video, VHC, one-tap approval",
      "MOT status pulled live from DVSA",
    ],
    icon: Car,
    accent: "primary",
    visual: "checkin",
  },
  {
    id: "workshop",
    n: "04",
    label: "Into the workshop",
    title: "Job lands with the technician.",
    sub: "Live on the dashboard, ready to start.",
    body: "Every active job sits on the workshop dashboard with a clear status pill — Draft, Sent, Viewed, Approved. Technicians know exactly what's waiting and can pick the next car off the ramp.",
    bullets: [
      "Status pills update in real time",
      "Search by reg, customer or vehicle",
      "KPIs: jobs awaiting reply, won, revenue",
    ],
    icon: Wrench,
    accent: "primary",
    visual: "workshop",
  },
  {
    id: "guided-video",
    n: "05",
    label: "Guided walkaround",
    title: "13-step guided inspection video.",
    sub: "The app coaches the technician through every check.",
    body: "Tap Record. The app prompts the technician through each step of the inspection — tyres, brakes, lights, fluids, undercar, bodywork. Every step becomes a chapter the customer can jump to.",
    bullets: [
      "Dynamic chapters per job item",
      "On-screen prompts keep it consistent",
      "One continuous video, fully chaptered",
    ],
    icon: Video,
    accent: "primary",
    visual: "video",
  },
  {
    id: "vhc",
    n: "06",
    label: "VHC sheet",
    title: "Technician fills in the paper VHC.",
    sub: "Green · Amber · Red — the system you already use.",
    body: "Whilst the car is on the ramp, the technician grades every component on the workshop's existing Vehicle Health Check sheet. No new paperwork. No process change.",
    bullets: [
      "Use your dealership's existing VHC",
      "Traffic-light grading per item",
      "Notes captured in the technician's hand",
    ],
    icon: ClipboardCheck,
    accent: "warning",
    visual: "vhc",
  },
  {
    id: "magic",
    n: "07",
    label: "The magic",
    title: "Snap the VHC. AI does the rest.",
    sub: "From paper to priced quote in seconds.",
    body: "The technician takes a photo of the completed VHC. ServiceSell PRO reads every line, extracts the items, matches them against your pre-loaded price matrix and writes a calm, plain-English explanation for the customer.",
    bullets: [
      "ServiceSell PRO AI reads the sheet",
      "Prices auto-fill from your catalog",
      "AI generates a customer-friendly summary",
    ],
    icon: Sparkles,
    accent: "primary",
    visual: "magic",
  },
  {
    id: "notify",
    n: "08",
    label: "Customer notified",
    title: "One link. Email and text.",
    sub: "Video, prices, urgency — all on their phone.",
    body: "The customer receives a single share link by email and SMS. They watch the technician's video, read the AI explanation, and see every recommendation with price and urgency colour-coded.",
    bullets: [
      "Email + SMS with secure link",
      "Mobile-first customer experience",
      "Spread-the-cost 0% calculator on every quote",
    ],
    icon: Send,
    accent: "primary",
    visual: "notify",
  },
  {
    id: "approve",
    n: "09",
    label: "Approve & go",
    title: "Customer authorises in seconds.",
    sub: "One tap per item. Work begins.",
    body: "The customer approves or declines each amber and red item. If they decline a critical safety item, a follow-up text 48 hours later brings around half of them back. The technician gets notified the moment the job is signed off.",
    bullets: [
      "Per-item approve / decline",
      "48h re-engagement for declined red items",
      "Workshop notified, work authorised",
    ],
    icon: ShieldCheck,
    accent: "success",
    visual: "approve",
  },
  {
    id: "snooze",
    n: "10",
    label: "Snooze, don't lose",
    title: "Declined today doesn't mean lost forever.",
    sub: "3 · 6 · 9-month reminders bring the work back round.",
    body: "When a customer declines an amber item, they get a calm card on the quote page: 'Want us to remind you when it's a good time to revisit?' One tap picks 3 / 6 / 9 months (or a custom date) and the work goes into the pipeline. On the day, an SMS + email fires with a deep-link back to the original quote — re-authorise in one tap. The Manager tab shows the £ value of work scheduled to come back.",
    bullets: [
      "Customer picks 3 / 6 / 9 months — or opts out",
      "Technician's recommendation pre-highlights the right chip",
      "Auto SMS + email on the due date with one-tap re-authorise",
      "Manager sees £ pipeline of recoverable work",
    ],
    icon: CalendarClock,
    accent: "warning",
    visual: "snooze",
  },
  {
    id: "handover",
    n: "11",
    label: "Handover & survey",
    title: "Cleaned, polished, keys back.",
    sub: "Thank-you video + CSI survey, all in one screen.",
    body: "Before the customer collects, the advisor ticks the handover checklist — washed, vacuumed, interior wiped, keys ready. The same share link they used to approve work now shows a thank-you video and a one-tap satisfaction survey link. Closing the loop builds the brand.",
    bullets: [
      "Handover checklist (wash, vacuum, interior, keys)",
      "Auto thank-you video on the customer's phone",
      "Customer satisfaction survey link surfaced",
    ],
    icon: Trophy,
    accent: "success",
    visual: "handover",
  },
  {
    id: "customer-brain",
    n: "12",
    label: "Customer Brain",
    title: "Every customer becomes a story.",
    sub: "One reg → lifetime spend, history, consent, what worked.",
    body: "Type a reg, name, or phone number into the Service Advisor tab. ServiceSell PRO surfaces the customer's entire lifetime with the workshop — every job, every approval, every decline, total spend, conversion %, GDPR consent status, and which reminders are scheduled to bring them back. The whole story on one screen, ready before they walk through the door.",
    bullets: [
      "Fuzzy search across name / phone / email / reg",
      "Lifetime spend, visits, conversion %, red recovery",
      "Full job timeline with every approval & decline",
      "GDPR consent audit trail at a glance",
    ],
    icon: Brain,
    accent: "primary",
    visual: "customer-brain",
  },
  {
    id: "handoff",
    n: "13",
    label: "See it live",
    title: "Now let me show you it live.",
    sub: "One click — a fully-loaded demo workshop.",
    body: "Everything you've just seen — booking, diary AI, walkaround, AI quote, plan-coverage check, service-plan upsell, group dashboard — is running right now against a pre-built demo workshop with a multi-brand vehicle mix (Ford, Vauxhall, VW, BMW, Volvo). Six live-stage jobs, two service-plan offers, real commission numbers on the board. Click below and the dealer principal can see exactly what their customers will see.",
    bullets: [
      "Pre-branded demo workshop (deep red over dark)",
      "6 jobs across every lifecycle stage — booked, sent, partial approval, completed",
      "Live commission card showing £10.88 due this month",
      "Hands-on — your dealer can click anything",
    ],
    icon: Rocket,
    accent: "primary",
    visual: "handoff",
  },
];

// Exported for the public cinematic /watch page (which reuses the same content)
export { STAGES };
// Small visual mockups — rendered with the live UI tokens so they
// always match the real app. Each is a stylised "screenshot" of the stage.
// Exported so the cinematic /watch experience can reuse them.
const URGENCY_DOT = {
  destructive: "bg-destructive",
  warning: "bg-warning",
  success: "bg-success",
  primary: "bg-primary",
};

export function StageVisual({ kind }) {
  switch (kind) {
    case "booking":
      return (
        <div className="card-default p-5 max-w-md mx-auto shadow-2xl">
          <div className="flex items-center justify-between">
            <div className="label-overline">Workshop diary</div>
            <span className="text-[10px] font-mono-plex text-muted-foreground">Wed, 04 Feb 2026</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {[
              { time: "08:30", car: "Yaris · YK21 PRO", st: "booked" },
              { time: "09:00", car: "Aygo · SP19 ABC", st: "in" },
              { time: "10:30", car: "Corolla · JC22 XYZ", st: "in" },
              { time: "11:00", car: "C-HR · CH20 DEF", st: "booked" },
              { time: "13:30", car: "RAV4 · RA21 GHI", st: "booked" },
              { time: "15:00", car: "Yaris Cross · YX22", st: "booked" },
            ].map((s, i) => (
              <div key={i} className={`rounded-md border p-2.5 ${s.st === "in" ? "border-primary bg-primary/10" : "border-border bg-muted/30"}`}>
                <div className="font-mono-plex text-[11px] text-muted-foreground">{s.time}</div>
                <div className="text-xs font-semibold mt-1 truncate">{s.car}</div>
                <div className={`text-[9px] font-bold uppercase tracking-wider mt-1 ${s.st === "in" ? "text-primary" : "text-muted-foreground"}`}>
                  {s.st === "in" ? "In workshop" : "Booked"}
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case "handover":
      return (
        <div className="grid sm:grid-cols-5 gap-4 max-w-2xl mx-auto items-center">
          {/* Checklist */}
          <div className="sm:col-span-3 card-default p-4 shadow-xl">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-success" />
              <div className="label-overline">Handover</div>
            </div>
            <div className="font-display font-bold text-lg mt-1">Ready for collection</div>
            <ul className="mt-3 space-y-2">
              {[
                ["Vehicle washed", true],
                ["Vacuumed", true],
                ["Interior wiped down", true],
                ["Keys ready for handover", true],
              ].map(([t, on], i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className={`w-4 h-4 ${on ? "text-success" : "text-muted-foreground"}`} />
                  <span className={on ? "" : "text-muted-foreground"}>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
              <Droplets className="w-3.5 h-3.5 text-primary" />
              <span>Mat shampoo · Interior fragrance · Glass clean</span>
            </div>
          </div>
          {/* Customer screen with thank-you + survey */}
          <div className="sm:col-span-2">
            <div className="card-default shadow-xl overflow-hidden">
              <div className="aspect-video bg-zinc-900 relative grid place-items-center">
                <Play className="w-9 h-9 text-white/90" fill="currentColor" />
                <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-success text-success-foreground text-[9px] font-bold uppercase">Thank you</div>
              </div>
              <div className="p-3 space-y-2">
                <div className="text-xs font-semibold">All done — see you next time</div>
                <a className="btn-primary text-[10px] w-full pointer-events-none">
                  <Sparkles className="w-3 h-3" /> Leave 60-sec feedback
                </a>
              </div>
            </div>
          </div>
        </div>
      );

    case "checkin":
      return (
        <div className="relative">
          <div className="card-default p-5 max-w-md mx-auto shadow-2xl">
            <div className="label-overline">Create</div>
            <div className="font-display font-bold text-2xl mt-1 tracking-tight">New job</div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-left">
              {[
                ["Customer", "Mr T. Walker"],
                ["Email", "tw@hotmail.co.uk"],
                ["Make", "Ford"],
                ["Model", "Yaris"],
                ["Year", "2021"],
                ["Reg", "YK21 PRO"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="label-overline">{k}</div>
                  <div className="mt-1 px-3 py-2 rounded-md bg-muted/40 border border-border text-sm font-mono-plex">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end">
              <div className="btn-primary text-sm pointer-events-none">Create job</div>
            </div>
          </div>
          <div className="absolute -bottom-3 -right-3 px-3 py-1.5 rounded-full bg-success/15 border border-success/30 text-success text-xs font-bold uppercase tracking-wider hidden md:flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> Job #YK21-PRO created
          </div>
        </div>
      );

    case "workshop":
      return (
        <div className="card-default max-w-xl mx-auto shadow-2xl overflow-hidden">
          <div className="bg-muted/40 px-5 py-3 flex items-center justify-between">
            <div className="label-overline">Workshop dashboard</div>
            <div className="text-xs font-mono-plex text-muted-foreground">3 active</div>
          </div>
          {[
            { c: "T. Walker", v: "2021 Ford Focus", reg: "YK21 PRO", s: "draft", style: "bg-secondary text-secondary-foreground" },
            { c: "S. Patel", v: "2019 Vauxhall Corsa", reg: "SP19 ABC", s: "viewed", style: "bg-warning/15 text-warning" },
            { c: "J. Chen", v: "2022 VW Golf", reg: "JC22 XYZ", s: "approved", style: "bg-success/15 text-success" },
          ].map((j, i) => (
            <div key={i} className="flex items-center justify-between px-5 py-3 border-t border-border">
              <div>
                <div className="font-semibold text-sm">{j.c}</div>
                <div className="text-xs text-muted-foreground">{j.v} · <span className="font-mono-plex">{j.reg}</span></div>
              </div>
              <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${j.style}`}>{j.s}</span>
            </div>
          ))}
        </div>
      );

    case "video":
      return (
        <div className="flex items-center justify-center gap-6 flex-wrap">
          {/* Phone */}
          <div className="relative w-[220px] h-[440px] rounded-[2.2rem] bg-black border-[8px] border-zinc-800 shadow-2xl overflow-hidden">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 rounded-full bg-zinc-900" />
            <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-black" />
            <div className="absolute inset-0 flex flex-col">
              <div className="px-4 pt-8 pb-3 flex items-center justify-between">
                <span className="text-white/80 text-[10px] font-bold uppercase tracking-widest">Step 04 / 13</span>
                <span className="px-1.5 py-0.5 rounded bg-destructive text-white text-[9px] font-bold uppercase">Rec</span>
              </div>
              <div className="flex-1 grid place-items-center">
                <div className="w-16 h-16 rounded-full bg-primary/20 grid place-items-center">
                  <div className="w-12 h-12 rounded-full bg-primary grid place-items-center">
                    <Video className="w-6 h-6 text-primary-foreground" />
                  </div>
                </div>
              </div>
              <div className="px-4 pb-5 space-y-2">
                <div className="text-white text-sm font-bold leading-tight">Front brake pads &amp; discs</div>
                <div className="text-white/60 text-[11px] leading-tight">Show wear edge, then rotor scoring.</div>
                <div className="h-1 bg-white/15 rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: "32%" }} />
                </div>
              </div>
            </div>
          </div>
          {/* Chapter list */}
          <div className="card-default p-4 w-[260px] shadow-xl text-left">
            <div className="label-overline">Chapters</div>
            <ul className="mt-3 space-y-1.5 text-sm">
              {[
                ["Walkaround", true],
                ["Tyres & pressures", true],
                ["Brake pads / discs", true],
                ["Suspension", false],
                ["Lights & bulbs", false],
                ["Fluids", false],
                ["Wipers", false],
                ["Undercar", false],
              ].map(([t, done], i) => (
                <li key={i} className="flex items-center gap-2">
                  {done ? (
                    <CheckCircle2 className="w-4 h-4 text-success" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-border" />
                  )}
                  <span className={done ? "text-foreground" : "text-muted-foreground"}>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );

    case "vhc":
      return (
        <div className="relative max-w-md mx-auto">
          <div className="bg-[#fdfcf6] text-zinc-900 rounded-md shadow-2xl p-5 -rotate-2 hover:rotate-0 transition-transform duration-500 border border-zinc-300">
            <div className="flex items-center justify-between border-b border-zinc-300 pb-2">
              <div className="font-bold text-sm tracking-tight">VEHICLE HEALTH CHECK</div>
              <div className="text-[10px] font-mono text-zinc-500">YK21 PRO</div>
            </div>
            <div className="mt-3 space-y-1.5 text-xs">
              {[
                ["Tyre tread — front", "G"],
                ["Tyre tread — rear", "A"],
                ["Front brake pads", "R"],
                ["Front brake discs", "R"],
                ["Rear brake pads", "G"],
                ["Suspension & bushes", "G"],
                ["Wiper blades", "A"],
                ["Coolant level", "G"],
                ["Lights — exterior", "G"],
              ].map(([item, grade], i) => (
                <div key={i} className="flex items-center justify-between border-b border-dashed border-zinc-200 pb-1">
                  <span className="font-medium">{item}</span>
                  <div className="flex items-center gap-1">
                    <span className={`w-3.5 h-3.5 rounded-full border ${grade === "G" ? "bg-emerald-500 border-emerald-600" : "border-zinc-300"}`} />
                    <span className={`w-3.5 h-3.5 rounded-full border ${grade === "A" ? "bg-amber-500 border-amber-600" : "border-zinc-300"}`} />
                    <span className={`w-3.5 h-3.5 rounded-full border ${grade === "R" ? "bg-rose-500 border-rose-600" : "border-zinc-300"}`} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[10px] text-zinc-500 flex justify-between">
              <span>Tech: M. Greaves</span>
              <span>Date: 04/02/26</span>
            </div>
          </div>
        </div>
      );

    case "magic":
      return (
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2">
          {/* Phone with camera */}
          <div className="relative w-[200px] h-[400px] rounded-[2rem] bg-black border-[8px] border-zinc-800 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 bg-zinc-950" />
            <div className="absolute inset-3 rounded-2xl border-2 border-primary/70 grid place-items-center">
              <Camera className="w-10 h-10 text-primary" />
              <div className="absolute bottom-3 left-3 right-3 text-center text-white/70 text-[10px] font-bold uppercase tracking-wider">Hold steady</div>
              <div className="absolute top-3 left-3 px-2 py-0.5 rounded bg-primary text-primary-foreground text-[9px] font-bold uppercase">Scanning</div>
            </div>
          </div>
          {/* Arrow with sparkle */}
          <div className="flex items-center gap-1.5 text-primary px-2 py-1.5">
            <Sparkles className="w-5 h-5 animate-pulse" />
            <ArrowRight className="w-6 h-6 hidden md:block" />
            <ArrowRight className="w-6 h-6 md:hidden rotate-90" />
            <Sparkles className="w-4 h-4 animate-pulse" />
          </div>
          {/* Generated quote */}
          <div className="card-default p-4 w-[280px] shadow-2xl text-left">
            <div className="label-overline">Auto-generated quote</div>
            <div className="font-display font-bold text-lg mt-1">5 items found</div>
            <ul className="mt-3 space-y-2 text-sm">
              {[
                { t: "Front brake pads", p: "£189.00", u: "destructive" },
                { t: "Front brake discs", p: "£245.00", u: "destructive" },
                { t: "Rear tyres (pair)", p: "£198.00", u: "warning" },
                { t: "Wiper blades", p: "£32.00", u: "warning" },
                { t: "Air filter", p: "£28.00", u: "success" },
              ].map((it, i) => (
                <li key={i} className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${URGENCY_DOT[it.u]}`} />
                    {it.t}
                  </span>
                  <span className="font-mono-plex text-xs">{it.p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-sm">
              <span className="label-overline">Subtotal</span>
              <span className="font-display font-bold">£692.00</span>
            </div>
          </div>
        </div>
      );

    case "notify":
      return (
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
          {/* SMS */}
          <div className="card-default p-4 shadow-xl">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-primary" />
              <div className="label-overline">SMS</div>
            </div>
            <div className="mt-3 px-3 py-2 rounded-2xl rounded-bl-sm bg-muted/50 text-sm leading-relaxed">
              Hi Tom — your Yaris (YK21 PRO) inspection is ready.
              <br />Watch the video &amp; approve work here:
              <br /><span className="text-primary underline">servicesell.pro/j/3hX9k</span>
            </div>
            <div className="mt-2 text-[10px] text-muted-foreground font-mono-plex">Delivered · 14:02</div>
          </div>
          {/* Email */}
          <div className="card-default shadow-xl overflow-hidden">
            <div className="px-4 py-2 bg-muted/40 flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              <div className="label-overline">Email</div>
            </div>
            <div className="px-4 py-3 text-sm">
              <div className="font-semibold">Your vehicle health check is ready</div>
              <div className="text-xs text-muted-foreground mt-1">From: workshop@toyota-service.example</div>
              <div className="mt-3 aspect-video rounded-md bg-zinc-900 grid place-items-center relative overflow-hidden">
                <Play className="w-8 h-8 text-white/90" fill="currentColor" />
                <div className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-mono-plex">02:14</div>
              </div>
              <div className="mt-3 text-xs leading-relaxed text-muted-foreground">
                Hi Tom, our technician Marcus has completed the health check on your Yaris…
              </div>
              <div className="mt-3 inline-flex px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-bold pointer-events-none">View & approve</div>
            </div>
          </div>
        </div>
      );

    case "approve":
      return (
        <div className="max-w-md mx-auto card-default shadow-2xl overflow-hidden">
          <div className="aspect-video bg-zinc-900 relative grid place-items-center">
            <Play className="w-10 h-10 text-white/90" fill="currentColor" />
            <div className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-mono-plex">02:14</div>
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider">Your inspection</div>
          </div>
          <div className="p-4">
            <div className="label-overline">Recommended work</div>
            {[
              { t: "Front brake pads + discs", p: "£434.00", u: "destructive", chosen: "approve" },
              { t: "Rear tyres (pair)", p: "£198.00", u: "warning", chosen: "approve" },
              { t: "Wiper blades", p: "£32.00", u: "warning", chosen: "decline" },
            ].map((it, i) => (
              <div key={i} className="mt-3 flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${URGENCY_DOT[it.u]} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{it.t}</div>
                  <div className="text-xs font-mono-plex text-muted-foreground">{it.p}</div>
                </div>
                {it.chosen === "approve" ? (
                  <span className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-success/15 text-success border border-success/30">Approved</span>
                ) : (
                  <span className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-destructive/15 text-destructive border border-destructive/30 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Declined
                  </span>
                )}
              </div>
            ))}
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
              <span className="label-overline">Authorised total</span>
              <span className="font-display font-bold text-lg">£632.00</span>
            </div>
          </div>
        </div>
      );

    case "diary-ai":
      return (
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2">
          {/* Phone photographing diary */}
          <div className="relative w-[200px] h-[400px] rounded-[2rem] bg-black border-[8px] border-zinc-800 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 bg-zinc-950" />
            <div className="absolute inset-3 rounded-2xl border-2 border-primary/70 grid place-items-center">
              <div className="absolute inset-3 rounded-lg bg-[#fdfcf6] text-zinc-900 p-2 text-[7px] leading-tight">
                <div className="text-center font-bold border-b border-zinc-300 pb-1">Tomorrow · Wed 12 Feb</div>
                {[
                  ["08:30", "T Walker", "YK21 PRO"],
                  ["09:00", "S Patel", "SP19 ABC"],
                  ["10:30", "J Chen", "JC22 XYZ"],
                  ["11:00", "L Murphy", "LM20 ABC"],
                  ["13:30", "K Smith", "KS21 DEF"],
                  ["15:00", "P Khan", "PK19 GHI"],
                ].map(([t, n, r], i) => (
                  <div key={i} className="flex justify-between border-b border-dashed border-zinc-200 py-0.5">
                    <span className="font-mono">{t}</span>
                    <span className="truncate mx-1">{n}</span>
                    <span className="font-mono">{r}</span>
                  </div>
                ))}
              </div>
              <Camera className="absolute bottom-2 right-2 w-5 h-5 text-primary/80" />
              <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-primary text-primary-foreground text-[8px] font-bold uppercase">Scanning</div>
            </div>
          </div>
          {/* Sparkle arrow */}
          <div className="flex items-center gap-1.5 text-primary px-2 py-1.5">
            <Sparkles className="w-5 h-5 animate-pulse" />
            <ArrowRight className="w-6 h-6 hidden md:block" />
            <ArrowRight className="w-6 h-6 md:hidden rotate-90" />
            <Sparkles className="w-4 h-4 animate-pulse" />
          </div>
          {/* Pre-call blast queue */}
          <div className="card-default p-4 w-[280px] shadow-2xl text-left">
            <div className="flex items-center justify-between">
              <div className="label-overline">Pre-call blast</div>
              <span className="text-[10px] font-mono-plex text-success">Ready</span>
            </div>
            <div className="font-display font-bold text-lg mt-1">6 of 6 selected</div>
            <ul className="mt-3 space-y-1.5 text-xs">
              {[
                { n: "T Walker", t: "08:30", st: "queued" },
                { n: "S Patel", t: "09:00", st: "queued", diag: true },
                { n: "J Chen", t: "10:30", st: "queued" },
                { n: "L Murphy", t: "11:00", st: "queued" },
              ].map((r, i) => (
                <li key={i} className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-success" />
                    <span className="font-semibold">{r.n}</span>
                    <span className="font-mono-plex text-muted-foreground">{r.t}</span>
                    {r.diag && <span className="px-1 py-0.5 rounded bg-warning/15 text-warning text-[8px] font-bold uppercase">Q</span>}
                  </span>
                  <span className="text-[9px] text-muted-foreground uppercase">SMS+Email</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 inline-flex w-full justify-center px-3 py-2 rounded-md bg-primary text-primary-foreground text-xs font-bold pointer-events-none">
              <Send className="w-3 h-3 mr-1.5" /> Send all (6)
            </div>
          </div>
        </div>
      );

    case "snooze":
      return (
        <div className="grid sm:grid-cols-5 gap-4 max-w-2xl mx-auto items-center">
          {/* Customer's snooze card */}
          <div className="sm:col-span-3 card-default p-4 shadow-2xl">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-warning" />
              <div className="label-overline">Wheel alignment · £99</div>
            </div>
            <div className="font-display font-bold text-base mt-1.5 leading-snug">Snooze, don't lose it.</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Our technician advised this work could wait. Want a reminder when it's a good time to revisit?
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                { m: "3 m" },
                { m: "6 m", rec: true },
                { m: "9 m" },
              ].map((c, i) => (
                <div
                  key={i}
                  className={`relative rounded-md border text-center py-2 text-sm font-bold ${
                    c.rec
                      ? "border-warning bg-warning/10 text-warning ring-2 ring-warning/30"
                      : "border-border bg-muted/30 text-foreground"
                  }`}
                >
                  {c.rec && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-warning text-warning-foreground text-[8px] font-black uppercase tracking-wider whitespace-nowrap">★ Recommended</span>
                  )}
                  {c.m}
                </div>
              ))}
            </div>
            <div className="mt-3 text-[10px] text-muted-foreground text-center">
              Or <span className="underline">pick a date</span> · <span className="underline">no thanks</span>
            </div>
          </div>
          {/* Manager pipeline preview */}
          <div className="sm:col-span-2 card-default p-4 shadow-xl">
            <div className="label-overline">Manager pipeline</div>
            <div className="font-display font-bold text-2xl mt-1">£8,420</div>
            <div className="text-[10px] text-muted-foreground">scheduled to come back</div>
            <ul className="mt-3 space-y-1.5 text-[11px]">
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">Scheduled</span>
                <span className="font-mono-plex font-bold">42</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">Re-approved</span>
                <span className="font-mono-plex font-bold text-success">17</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">Conversion</span>
                <span className="font-mono-plex font-bold text-primary">41%</span>
              </li>
            </ul>
          </div>
        </div>
      );

    case "customer-brain":
      return (
        <div className="card-default max-w-xl mx-auto shadow-2xl overflow-hidden">
          {/* Search header */}
          <div className="bg-muted/40 px-5 py-3 flex items-center gap-2 border-b border-border">
            <Search className="w-4 h-4 text-primary" />
            <span className="font-mono-plex text-xs">YK21 PRO</span>
            <span className="ml-auto text-[10px] text-muted-foreground">1 match</span>
          </div>
          {/* Customer hero */}
          <div className="p-5">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/15 grid place-items-center font-display font-black text-primary">TW</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg leading-tight">Mr T. Walker</div>
                <div className="text-xs text-muted-foreground">2021 Ford Focus · <span className="font-mono-plex">YK21 PRO</span> · MOT: 14 Oct 2026</div>
              </div>
              <div className="hidden sm:flex flex-col items-end">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                <Mail className="w-3.5 h-3.5 text-muted-foreground mt-1" />
              </div>
            </div>
            {/* Lifetime KPIs */}
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              {[
                { l: "Lifetime", v: "£2,418", a: "primary" },
                { l: "Visits", v: "7", a: "muted" },
                { l: "Conv %", v: "62%", a: "success" },
                { l: "Red back", v: "3", a: "warning" },
              ].map((k, i) => (
                <div key={i} className="rounded-md bg-muted/30 border border-border p-2">
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{k.l}</div>
                  <div className={`font-display font-bold text-sm mt-0.5 ${
                    k.a === "primary" ? "text-primary" :
                    k.a === "success" ? "text-success" :
                    k.a === "warning" ? "text-warning" : ""
                  }`}>{k.v}</div>
                </div>
              ))}
            </div>
            {/* Mini timeline */}
            <div className="mt-4">
              <div className="label-overline">Job history</div>
              <ul className="mt-2 space-y-1.5">
                {[
                  { d: "04 Feb 26", t: "Brakes & tyres", v: "£632", s: "approved" },
                  { d: "12 Sep 25", t: "Service + MOT", v: "£298", s: "approved" },
                  { d: "08 Mar 25", t: "Wheel alignment", v: "£99", s: "snoozed" },
                ].map((j, i) => (
                  <li key={i} className="flex items-center justify-between text-xs py-1 border-b border-dashed border-border/50 last:border-0">
                    <span className="font-mono-plex text-muted-foreground">{j.d}</span>
                    <span className="font-semibold truncate mx-2 flex-1">{j.t}</span>
                    <span className="font-mono-plex">{j.v}</span>
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider ${
                      j.s === "approved" ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
                    }`}>{j.s}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* GDPR + consent chip */}
            <div className="mt-4 pt-3 border-t border-border flex items-center gap-2 text-[10px]">
              <ShieldCheck className="w-3.5 h-3.5 text-success" />
              <span className="text-muted-foreground">Data processing ✓ · Marketing ✓ · captured 12 Sep 25</span>
            </div>
          </div>
        </div>
      );

    case "handoff":
      return (
        <div className="text-center px-6 py-10 rounded-xl bg-gradient-to-br from-primary/15 via-card to-background border-2 border-primary/30 max-w-md mx-auto">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/15 ring-2 ring-primary/40 mb-4">
            <Rocket className="w-7 h-7 text-primary" />
          </div>
          <div className="label-overline text-primary mb-2">Live demo handoff</div>
          <div className="font-display font-bold text-lg leading-tight">
            Drop into a demo service centre in one click.
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            (Final slide in presentation mode triggers the demo workshop launch.)
          </div>
        </div>
      );

    default:
      return null;
  }
}

// =====================================================================
// Handoff slide — the final "Now let me show you it live" slide that
// pivots from the static deck into the interactive
// demo workshop. One-click — spins up a fresh demo tenant + lands
// the presenter on the dashboard logged in as Steve Kirby.
// =====================================================================
function HandoffSlide({ stage, total }) {
  const Icon = stage.icon;
  const nav = useNavigate();
  const { launchToyotaDemo } = useAuth();
  const [busy, setBusy] = useState(false);

  const launch = async () => {
    setBusy(true);
    try {
      const { credentials } = await launchToyotaDemo();
      toast.success(`Demo workshop ready · login: ${credentials.email}`, { duration: 8000 });
      nav("/dashboard");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't spin up the demo — please try again";
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10 items-center" data-testid={`slide-${stage.id}`}>
      <div className="lg:col-span-5 text-left">
        <div className="flex items-center gap-3">
          <span className="font-mono-plex text-sm font-bold text-primary">{stage.n} / {String(total).padStart(2, "0")}</span>
          <span className="h-px flex-1 bg-border" />
          <div className="w-10 h-10 rounded-full bg-primary/15 grid place-items-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
        </div>
        <div className="label-overline mt-6">{stage.label}</div>
        <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-[1.05] mt-3">
          {stage.title}
        </h2>
        <p className="mt-3 text-primary font-semibold text-lg">{stage.sub}</p>
        <p className="mt-5 text-foreground/80 leading-relaxed max-w-lg">{stage.body}</p>
        <ul className="mt-6 space-y-2">
          {stage.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-7">
        <div className="relative p-8 md:p-12 rounded-xl bg-gradient-to-br from-primary/10 via-card to-background border-2 border-primary/30 min-h-[420px] flex flex-col items-center justify-center text-center overflow-hidden">
          <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 50% 30%, hsl(var(--primary) / 0.25), transparent 65%)" }} />

          <div className="relative">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-primary/15 ring-2 ring-primary/40 mb-6">
              <Rocket className="w-12 h-12 text-primary" />
            </div>
            <div className="label-overline text-primary mb-3">Live demo · Service centre</div>
            <h3 className="font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight mb-4">
              Ready to see it for real?
            </h3>
            <p className="text-foreground/70 max-w-md mx-auto mb-8">
              One click. A fresh, fully-loaded demo workshop will spin up, and you'll be inside the product within 2 seconds.
            </p>
            <button
              onClick={launch}
              disabled={busy}
              className="inline-flex items-center justify-center gap-3 px-8 py-5 rounded-full bg-primary text-primary-foreground font-bold text-base hover:scale-[1.02] transition-transform disabled:opacity-60 shadow-2xl shadow-primary/30"
              data-testid="handoff-launch-toyota-demo-btn"
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
              {busy ? "Spinning up demo workshop…" : "Launch the demo workshop"}
              {!busy && <ArrowRight className="w-5 h-5" />}
            </button>
            <p className="mt-5 text-[11px] text-muted-foreground">
              You'll land in the workshop as <strong className="text-foreground">Steve Kirby</strong>. Re-runnable any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// One slide (presentation mode)
// =====================================================================
function Slide({ stage, index, total }) {
  const Icon = stage.icon;
  return (
    <div className="grid lg:grid-cols-12 gap-10 items-center" data-testid={`slide-${stage.id}`}>
      <div className="lg:col-span-5 text-left">
        <div className="flex items-center gap-3">
          <span className="font-mono-plex text-sm font-bold text-primary">{stage.n} / {String(total).padStart(2, "0")}</span>
          <span className="h-px flex-1 bg-border" />
          <div className="w-10 h-10 rounded-full bg-primary/15 grid place-items-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
        </div>
        <div className="label-overline mt-6">{stage.label}</div>
        <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-[1.05] mt-3">
          {stage.title}
        </h2>
        <p className="mt-3 text-primary font-semibold text-lg">{stage.sub}</p>
        <p className="mt-5 text-foreground/80 leading-relaxed max-w-lg">{stage.body}</p>
        <ul className="mt-6 space-y-2">
          {stage.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-7">
        <div className="relative p-4 md:p-8 rounded-xl bg-gradient-to-br from-card to-background border border-border min-h-[420px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, hsl(var(--primary) / 0.18), transparent 60%)" }} />
          <div className="relative w-full">
            <StageVisual kind={stage.visual} />
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Overview mode — all 7 stages connected on one page (great for print)
// =====================================================================
function OverviewMode({ publicMode = false }) {
  return (
    <div className="space-y-8" data-testid="overview-mode">
      <div className="text-center max-w-2xl mx-auto">
        <div className="label-overline">The full process</div>
        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-3">
          From <span className="text-primary">key handover</span> to <span className="text-primary">authorised work</span> — in one screen.
        </h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.id} className="card-default p-5 relative hover:-translate-y-1 transition-transform" data-testid={`overview-card-${s.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono-plex text-xs font-bold text-primary">{s.n}</span>
                <Icon className="w-4 h-4 text-foreground/60" />
              </div>
              <div className="label-overline mt-4">{s.label}</div>
              <div className="font-display font-bold text-lg mt-1 leading-snug">{s.title}</div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{s.body}</p>
              {/* Connector arrow except for last */}
              {i < STAGES.length - 1 && (
                <ArrowRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary/60 bg-background rounded-full" />
              )}
            </div>
          );
        })}
      </div>

      {/* Visual strip */}
      <div className="card-default p-6 mt-10">
        <div className="label-overline">Where the magic happens</div>
        <h3 className="font-display font-bold text-2xl mt-2">Stage 05 — paper to priced quote.</h3>
        <div className="mt-6">
          <StageVisual kind="magic" />
        </div>
      </div>

      <div className="text-center pt-6">
        <Link to={publicMode ? "/pilot" : "/dashboard"} className="btn-primary text-base" data-testid="overview-cta-btn">
          {publicMode ? "Book a discovery call" : "Back to dashboard"} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

// =====================================================================
// Kiosk view — full-screen presentation, hides all chrome
// =====================================================================
function KioskView({ stage, slide, total, onPrev, onNext, onExit, autoplay, onToggleAutoplay }) {
  const Icon = stage.icon;
  return (
    <div className="fixed inset-0 z-[60] bg-background flex flex-col" data-testid="kiosk-view">
      {/* Top thin bar */}
      <div className="px-8 py-5 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-primary/15 grid place-items-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="label-overline text-[10px]">{stage.label}</div>
            <div className="font-mono-plex text-xs text-muted-foreground">{stage.n} / 0{total}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onToggleAutoplay} className="btn-outline" data-testid="kiosk-autoplay-btn" title="Autoplay (P)">
            {autoplay ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button onClick={onExit} className="btn-outline" data-testid="kiosk-exit-btn" title="Exit full-screen (Esc)">
            <Minimize2 className="w-4 h-4" /> Exit
          </button>
        </div>
      </div>

      {/* Slide body — centred, large */}
      <div className="flex-1 grid place-items-center px-12 py-8 overflow-hidden">
        <div key={stage.id} className="w-full max-w-[1400px] grid lg:grid-cols-12 gap-12 items-center animate-fade-up">
          <div className="lg:col-span-5 text-left">
            <div className="label-overline">{stage.label}</div>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] mt-3 text-5xl xl:text-6xl 2xl:text-7xl">
              {stage.title}
            </h2>
            <p className="mt-5 text-primary font-semibold text-xl xl:text-2xl">{stage.sub}</p>
            <p className="mt-6 text-foreground/80 leading-relaxed text-lg max-w-xl">{stage.body}</p>
            <ul className="mt-7 space-y-2.5">
              {stage.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-base">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="relative p-6 md:p-10 rounded-2xl bg-gradient-to-br from-card to-background border border-border min-h-[460px] flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, hsl(var(--primary) / 0.22), transparent 60%)" }} />
              <div className="relative w-full">
                <StageVisual kind={stage.visual} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="px-8 py-5 border-t border-border flex items-center justify-between">
        <button onClick={onPrev} disabled={slide === 0} className="btn-outline disabled:opacity-30" data-testid="kiosk-prev-btn">
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        <div className="flex-1 mx-6 h-1 bg-border rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${((slide + 1) / total) * 100}%` }} />
        </div>
        <button onClick={onNext} disabled={slide === total - 1} className="btn-primary disabled:opacity-30" data-testid="kiosk-next-btn">
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Main page
// =====================================================================
export default function HowItWorksPage({ publicMode = false }) {
  const nav = useNavigate();
  const [view, setView] = useState("slides"); // "slides" | "overview"
  const [slide, setSlide] = useState(0);
  const [autoplay, setAutoplay] = useState(publicMode);
  const [kiosk, setKiosk] = useState(false);
  const total = STAGES.length;

  const stage = useMemo(() => STAGES[slide], [slide]);

  const next = useCallback(() => setSlide((s) => Math.min(total - 1, s + 1)), [total]);
  const prev = useCallback(() => setSlide((s) => Math.max(0, s - 1)), []);

  const enterKiosk = async () => {
    setView("slides");
    setKiosk(true);
    try { await document.documentElement.requestFullscreen?.(); } catch { /* ignored */ }
  };
  const exitKiosk = async () => {
    setKiosk(false);
    setAutoplay(false);
    try { if (document.fullscreenElement) await document.exitFullscreen?.(); } catch { /* ignored */ }
  };

  // Reflect browser fullscreen exit back into state (e.g. user pressed F11/Esc)
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement && kiosk) setKiosk(false); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [kiosk]);

  // Keyboard navigation
  useEffect(() => {
    if (view !== "slides") return;
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") { if (kiosk) exitKiosk(); else if (!publicMode) nav("/dashboard"); else nav("/"); }
      else if (e.key.toLowerCase() === "o" && !kiosk) setView("overview");
      else if (e.key.toLowerCase() === "p") setAutoplay((a) => !a);
      else if (e.key.toLowerCase() === "f") { if (kiosk) exitKiosk(); else enterKiosk(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, slide, nav, kiosk, publicMode, next, prev]);

  // Autoplay timer
  useEffect(() => {
    if (!autoplay || view !== "slides") return;
    const t = setTimeout(() => {
      if (slide >= total - 1) setAutoplay(false);
      else next();
    }, 6500);
    return () => clearTimeout(t);
  }, [autoplay, slide, view, total, next]);

  return (
    <div className={`min-h-screen bg-background ${kiosk ? "kiosk-mode" : ""}`} data-testid="how-it-works-page">
      {kiosk && (
        <KioskView
          stage={stage}
          slide={slide}
          total={total}
          onPrev={prev}
          onNext={next}
          onExit={exitKiosk}
          autoplay={autoplay}
          onToggleAutoplay={() => setAutoplay((a) => !a)}
        />
      )}

      {/* Top bar */}
      <header className={`border-b border-border bg-card sticky top-0 z-30 print:hidden ${kiosk ? "hidden" : ""}`}>
        <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 h-16 flex items-center justify-between">
          <BrandLink />
          <div className="flex items-center gap-2">
            <Link to={publicMode ? "/" : "/dashboard"} className="btn-outline" data-testid="howto-back-btn">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">{publicMode ? "Back" : "Dashboard"}</span>
            </Link>
            <div className="hidden md:flex items-center gap-1 ml-1 p-1 rounded-lg bg-muted/40 border border-border">
              <button
                onClick={() => setView("slides")}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${view === "slides" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="view-slides-btn"
              >
                <Presentation className="w-4 h-4" /> Slides
              </button>
              <button
                onClick={() => setView("overview")}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${view === "overview" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="view-overview-btn"
              >
                <LayoutGrid className="w-4 h-4" /> Overview
              </button>
            </div>
            <button onClick={() => window.print()} className="btn-outline hidden sm:inline-flex" data-testid="howto-print-btn" title="Print / save as PDF">
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Page header */}
      <div className={`border-b border-border print:hidden ${kiosk ? "hidden" : ""}`}>
        <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-5 sm:px-6 py-6 sm:py-8 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0 max-w-3xl">
            <div className="label-overline">How ServiceSell PRO works</div>
            {/* Title — text-balance keeps the line breaks tidy on mobile, the
                em-dash sits on its own line on phones rather than splitting awkwardly. */}
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter mt-2 text-balance leading-[1.1]">
              From key handover to{" "}
              <span className="text-primary">authorised work</span>
              <span className="hidden sm:inline"> — in one screen.</span>
            </h1>
            {/* Description: mobile-first, with desktop keyboard hints only shown on sm+. */}
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Walk a dealership manager or new technician through the whole flow — every stage from customer book-in to the moment work is authorised.
            </p>
            <p className="hidden sm:block text-[11px] text-muted-foreground/70 mt-2 font-mono-plex">
              ← → navigate · <span>P</span> autoplay · <span>O</span> overview · <span>F</span> full-screen
            </p>
            <p className="sm:hidden text-[11px] text-muted-foreground/70 mt-2">
              Tap <span className="font-bold text-foreground/80">Next</span> at the bottom of each slide, or use the dots to jump.
            </p>
          </div>
          {view === "slides" && (
            <div className="flex items-center gap-2 w-full sm:w-auto" data-testid="slide-header-actions">
              <button
                onClick={enterKiosk}
                className="btn-primary flex-1 sm:flex-none justify-center"
                data-testid="present-mode-btn"
                title="Full-screen presentation (F)"
              >
                <Maximize2 className="w-4 h-4" /> Present
              </button>
              <button
                onClick={() => setAutoplay((a) => !a)}
                className="btn-outline flex-1 sm:flex-none justify-center"
                data-testid="autoplay-btn"
                title="Autoplay (P)"
              >
                {autoplay ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {autoplay ? "Pause" : "Autoplay"}
              </button>
              {/* Slide counter — hidden on mobile to avoid duplicating the
                  in-slide "01 / 13" indicator that sits two scroll-flicks away. */}
              <span className="hidden sm:inline font-mono-plex text-sm text-muted-foreground" data-testid="slide-counter">
                {String(slide + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <main className={`max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 py-10 md:py-14 ${kiosk ? "hidden" : ""}`}>
        {view === "slides" ? (
          <>
            <div key={stage.id} className="animate-fade-up">
              {stage.id === "handoff"
                ? <HandoffSlide stage={stage} total={total} />
                : <Slide stage={stage} index={slide} total={total} />}
            </div>

            {/* Slide nav */}
            <div className="mt-12 flex items-center justify-between gap-4">
              <button
                onClick={prev}
                disabled={slide === 0}
                className="btn-outline disabled:opacity-40"
                data-testid="slide-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>

              {/* Dots */}
              <div className="flex items-center gap-1.5" data-testid="slide-dots">
                {STAGES.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setSlide(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    data-testid={`slide-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all ${i === slide ? "bg-primary w-8" : "bg-border hover:bg-muted-foreground w-3"}`}
                  />
                ))}
              </div>

              {slide < total - 1 ? (
                <button onClick={next} className="btn-primary" data-testid="slide-next-btn">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <Link to={publicMode ? "/pilot" : "/dashboard"} className="btn-primary" data-testid="slide-finish-btn">
                  {publicMode ? "Book a discovery call" : "Try a demo job"} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>

            {/* Progress */}
            <div className="mt-6 h-1 bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${((slide + 1) / total) * 100}%` }}
                data-testid="slide-progress"
              />
            </div>
          </>
        ) : (
          <OverviewMode publicMode={publicMode} />
        )}
      </main>

      {/* Mobile view-mode FAB — sits above the "Made with Emergent" platform
          badge that lives at bottom-right. `bottom-20` clears the badge so
          they don't visually collide on phones. */}
      <div className="md:hidden fixed bottom-20 right-4 z-30 flex gap-2 print:hidden">
        <button
          onClick={() => setView(view === "slides" ? "overview" : "slides")}
          className="btn-primary shadow-2xl"
          data-testid="view-toggle-mobile-btn"
        >
          {view === "slides" ? <LayoutGrid className="w-4 h-4" /> : <Presentation className="w-4 h-4" />}
          {view === "slides" ? "Overview" : "Slides"}
        </button>
      </div>
    </div>
  );
}
